/**
 * AgentContextProvider
 *
 * Provides module-specific context (scenario ID, network ID, etc.) to all
 * AI agent components in the subtree. This replaces the hardcoded moduleId
 * prop drilling in CopilotPanel.
 *
 * Usage:
 *   <AgentContextProvider moduleId="model-builder" scenarioId="sc-123">
 *     <ModuleContent />
 *     <MangoAIPanel />
 *   </AgentContextProvider>
 */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { ModuleContext } from '@/types/agent';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AgentContext = createContext<ModuleContext | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface AgentContextProviderProps {
  children: ReactNode;
  /** Required module identifier. */
  moduleId: string;
  /** Optional IDs that narrow the context for agent tools. */
  scenarioId?: string;
  networkId?: string;
  executionId?: string;
  baselineId?: string;
  iso?: string;
  portfolioId?: string;
}

export function AgentContextProvider({
  children,
  moduleId,
  scenarioId,
  networkId,
  executionId,
  baselineId,
  iso,
  portfolioId,
}: AgentContextProviderProps): JSX.Element {
  const value = useMemo<ModuleContext>(
    () => ({
      module_id: moduleId,
      scenario_id: scenarioId,
      network_id: networkId,
      execution_id: executionId,
      baseline_id: baselineId,
      iso,
      portfolio_id: portfolioId,
    }),
    [moduleId, scenarioId, networkId, executionId, baselineId, iso, portfolioId],
  );

  return (
    <AgentContext.Provider value={value}>{children}</AgentContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Read the current module context provided by AgentContextProvider.
 *
 * Throws if called outside the provider tree so that missing context is
 * caught at development time rather than silently producing undefined.
 */
export function useAgentContext(): ModuleContext {
  const ctx = useContext(AgentContext);
  if (ctx === null) {
    throw new Error(
      'useAgentContext must be used inside <AgentContextProvider>. ' +
        'Wrap your module page with AgentContextProvider.',
    );
  }
  return ctx;
}
